import { DOCUMENT } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CasesBackendService, MotivatorBackendService, MotivatorSocketService, P2pApiNewService } from '@dev-fast/backend-services';
import { UpgraderBackendService } from '@dev-fast/backend-services/games/upgrader';
import type {
  AnimationSpeed,
  IActivities,
  IBiggestCountCasesDto,
  IBonusPiecesDto,
  IDropItemDto,
  IMotivatorDto,
  IMotivatorStatistic,
  IMotivatorStatisticUpgrader,
  IMotivatorTop24Item,
  IMotivatorType,
  IP2pItemCreated,
  IP2pItemCreatedItem,
  IUpgraderItem,
} from '@dev-fast/types';
import {
  EAgentKey,
  EP2pItemCreatedStatuses,
  mapTop24ItemDto,
  MotivatorReqKeys,
  MotivatorReqTypes,
  MotivatorSocketKeys,
  MotivatorSource,
  NotificationStatus,
  NotificationType,
  PROJECT,
  Widgets,
} from '@dev-fast/types';
import type { IP2PMotivatorStatistic } from '@dev-fast/types/entities';
import { type StateContext, Action, Selector, State, Store } from '@ngxs/store';
import { type StateOperator, compose, insertItem, patch, removeItem } from '@ngxs/store/operators';
import { reverse, uniq, uniqueId } from 'lodash-es';
import type { Observable, OperatorFunction, Subscription } from 'rxjs';
import {
  buffer,
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  map,
  merge,
  of,
  share,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { EnvironmentService } from '@app/core/environment-service';
import { LanguageState } from '@app/core/language-service';
import { NotificationsService } from '@app/core/notification-service';
import { MediaPreferencesState, SetCurrentGameActivities } from '@app/core/state/games-store';
import { UserService } from '@app/core/user-service';
import { IS_SERVER_TOKEN } from '@app/shared/utils';

import {
  MIN_MOTIVATOR_ITEMS,
  MIN_PROFIT_OVERPRICE,
  MIN_TICKETS_MOTIVATOR_ITEMS,
  OPENING_ANIM_DURATION,
  OUT_FROM_ANIMATION_DURATION,
} from '../../shared/constants';
import { MIN_CASES_MOTIVATOR_ITEMS, MIN_CASES_MOTIVATOR_VISIBLE_ITEMS } from '../../shared/constants/constants';
import {
  AddBestLastAllWon,
  AddBestLastShardsWon,
  AddBestLastWon,
  AddBestResultStat,
  AddLastTicketItem,
  AddLastWon,
  AddTradeHistoryItem,
  ClearCasesMotivator,
  GetBestLastResultStat,
  GetCurrentCaseMotivator,
  GetLobbyCasesMotivator,
  GetMarketStat,
  GetMotivatorTypes,
  GetShardsStat,
  GetStat,
  GetTopUsers,
  GetUpgraderStat,
  ResetPartialState,
  SetActiveMotivatorSocket,
  SubscribeToLastWon,
  ToggleDropList,
  UnsetActiveMotivatorSocket,
  UnsubscribeToLastWon,
  UpdateCurrentCaseMotivator,
  UpdateLobbyCasesMotivator,
} from './motivator.actions';
import type { IMotivatorStateModel } from './motivator.model';
import { MOTIVATOR_INITIAL_STATE } from './motivator.model';

const BEST_LAST_ALL_PREFIX = 'best_last_all_';
const BEST_LAST_PREFIX = 'best_last_';
const LAST_PREFIX = 'last_';
const BEST_DROP = 'best_drop_';
const SHARD_PREFIX = 'shard_';

@State<IMotivatorStateModel>({
  defaults: MOTIVATOR_INITIAL_STATE,
  name: 'motivator',
})
@Injectable()
export class MotivatorState {
  // Подключение сервисов
  readonly #isServer = inject(IS_SERVER_TOKEN);
  readonly #casesBackendService = inject(CasesBackendService);
  readonly #document = inject(DOCUMENT);
  readonly #environmentService = inject(EnvironmentService);
  readonly #motivatorBackendService = inject(MotivatorBackendService);
  readonly #motivatorSocketService = inject(MotivatorSocketService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #store = inject(Store);
  readonly #upgraderBackendService = inject(UpgraderBackendService);
  readonly #p2pApiNewService = inject(P2pApiNewService);
  readonly #userService = inject(UserService);

  // Переменные сервисов
  readonly #isProjectMarket = signal(this.#environmentService.isProject({ name: PROJECT.MARKET }));
  readonly #visibilityChange$: Observable<boolean> = fromEvent(this.#document, 'visibilitychange').pipe(
    startWith(true),
    map(() => this.#document.visibilityState !== 'hidden'),
  );

  #getLang(): string {
    return this.#store.selectSnapshot(LanguageState.lang).path.substring(0, 2).toUpperCase();
  }

  readonly #pageInFocus = toSignal(this.#visibilityChange$, { requireSync: true });

  #subscribeStream: Subscription | null = null;

  // Селекторы
  // НЕ БРАТЬ напрямую, а подписываться через motivator.service!
  @Selector()
  static bestDrop({ bestDrop }: IMotivatorStateModel): IMotivatorDto<IDropItemDto> | null {
    return bestDrop;
  }

  @Selector()
  static bigOpens({ bigOpens }: IMotivatorStateModel): IBiggestCountCasesDto | null {
    return bigOpens;
  }

  @Selector()
  static marketList({ marketList }: IMotivatorStateModel): Array<IMotivatorDto<IP2pItemCreated>> {
    return marketList;
  }

  @Selector()
  static bestLastShardsDropList({ bestLastShardsDropList }: IMotivatorStateModel): Array<IMotivatorDto<IBonusPiecesDto>> {
    return bestLastShardsDropList.map((motivator) => ({
      ...motivator,
      data: {
        ...motivator.data,
        bonusPieces: motivator.data.bonusPieces / 100,
      },
    }));
  }

  @Selector()
  static bestDropsList({ bestDropsList }: IMotivatorStateModel): Array<IMotivatorDto<IDropItemDto>> {
    return bestDropsList;
  }

  @Selector()
  static bestDropsListAll({ bestDropsListAll }: IMotivatorStateModel): Array<IMotivatorDto<IDropItemDto>> {
    return bestDropsListAll;
  }

  @Selector()
  static bestUpgradesList({ bestUpgradesList }: IMotivatorStateModel): Array<IMotivatorDto<IUpgraderItem>> {
    return bestUpgradesList;
  }

  @Selector()
  static currentCaseMotivator({ currentCaseMotivator }: IMotivatorStateModel): IMotivatorStatistic {
    return currentCaseMotivator;
  }

  @Selector()
  static isCaseMotivatorHidden({ isCaseMotivatorHidden }: IMotivatorStateModel): boolean {
    return isCaseMotivatorHidden;
  }

  @Selector()
  static lobbyCasesMotivator({ lobbyCasesMotivator }: IMotivatorStateModel): IMotivatorStatistic {
    return lobbyCasesMotivator;
  }

  @Selector()
  static lastDropsList({ lastDropsList }: IMotivatorStateModel): Array<IMotivatorDto<IDropItemDto>> {
    return lastDropsList;
  }

  @Selector()
  static bestLastResult({ bestLastResult }: IMotivatorStateModel): IMotivatorTop24Item[] {
    return bestLastResult;
  }

  @Selector()
  static lastUpgradesList({ lastUpgradesList }: IMotivatorStateModel): Array<IMotivatorDto<IUpgraderItem>> {
    return lastUpgradesList;
  }

  @Selector()
  static lastTicketsItemsList({ lastTicketsItemsList }: IMotivatorStateModel): Array<IMotivatorDto<IDropItemDto>> {
    return lastTicketsItemsList;
  }

  @Selector()
  static topUsers({ topUsers }: IMotivatorStateModel): IMotivatorType | null {
    return topUsers;
  }

  @Selector()
  static casesLast({ casesLast }: IMotivatorStateModel): Array<IMotivatorDto<IDropItemDto>> {
    return casesLast;
  }

  @Selector()
  static upgradesLast({ upgradesLast }: IMotivatorStateModel): Array<IMotivatorDto<IUpgraderItem>> {
    return upgradesLast;
  }

  @Selector()
  static challengesLast({ challengesLast }: IMotivatorStateModel): Array<IMotivatorDto<IDropItemDto>> {
    return challengesLast;
  }

  // Actions

  @Action(GetMotivatorTypes)
  getMotivatorTypes({ setState }: StateContext<IMotivatorStateModel>): Observable<IMotivatorType> {
    return this.#motivatorBackendService
      .getMotivatorTypes({
        limit: 12,
        keys: [MotivatorReqKeys.LAST, MotivatorReqKeys.BEST_LAST, MotivatorReqKeys.BEST_LAST_ALL],
        types: [MotivatorReqTypes.CASES, MotivatorReqTypes.UPGRADE, MotivatorReqTypes.CASE_BATTLES, MotivatorReqTypes.BONUS_WALLET_ITEM],
        lang: this.#getLang(),
      })
      .pipe(
        tap((data: IMotivatorType) => {
          setState(
            patch({
              casesLast: data.cases?.last ?? [],
              upgradesLast: data.upgrade?.last ?? [],
              challengesLast: data['challenges']?.last ?? [],
              lastTicketsItemsList: data['bonus-wallet-item']?.last ?? [],
            }),
          );
        }),
      );
  }

  @Action(GetTopUsers)
  getTopUsers({ setState }: StateContext<IMotivatorStateModel>, { limit }: GetTopUsers): Observable<IMotivatorType> {
    return this.#motivatorBackendService
      .getMotivatorTypes({
        limit: limit ?? 1,
        types: [
          MotivatorReqTypes.MARKET,
          MotivatorReqTypes.CASES,
          MotivatorReqTypes.UPGRADE,
          MotivatorReqTypes.CASE_BATTLES,
          MotivatorReqTypes.BONUS_WALLET,
        ],
        keys: [
          MotivatorReqKeys.LARGEST_AMOUNT,
          MotivatorReqKeys.LARGEST_AMOUNT_PROFIT,
          MotivatorReqKeys.BIG_OPENS,
          MotivatorReqKeys.MOST_EXPENSIVE_ITEM,
          MotivatorReqKeys.BEST_DROP,
        ],
        lang: this.#getLang(),
      })
      .pipe(tap((data: IMotivatorType) => setState(patch({ topUsers: data }))));
  }

  @Action(AddBestLastAllWon)
  addBestLastAllWon({ getState, setState }: StateContext<IMotivatorStateModel>, { motivatorDto }: AddBestLastAllWon): void {
    const { bestDropsListAll, isPlayback } = getState();

    if (isPlayback) {
      setState(patch({ bestDropsListAll: compose(insertItem(motivatorDto, 0), removeItem(bestDropsListAll.length)) }));
    }
  }

  @Action(AddBestLastWon)
  addBestLastWon({ dispatch, getState, setState }: StateContext<IMotivatorStateModel>, { motivatorDto }: AddBestLastWon): void {
    const { caseId, isPlayback, bestUpgradesList } = getState();

    const dropItems = motivatorDto.filter(this.#isDropItem);
    const upgradeItems = motivatorDto.filter(this.#isUpgradeItem);

    if (dropItems.length) {
      dropItems.forEach((motivator) => {
        if (isPlayback && caseId && caseId === motivator.data.case.id) {
          motivator.data.isBestItemsUpdated = true;
          dispatch(new UpdateCurrentCaseMotivator(motivator.data));
        }
      });
    }

    if (upgradeItems.length) {
      const newBestUpgradesList = reverse(upgradeItems).concat(bestUpgradesList);
      newBestUpgradesList.splice(newBestUpgradesList.length - upgradeItems.length);
      setState(patch({ bestUpgradesList: newBestUpgradesList }));
    }
  }

  @Action(AddLastWon)
  addLastWon({ dispatch, getState, setState }: StateContext<IMotivatorStateModel>, { motivatorsDto }: AddLastWon): void {
    const { isPlayback } = getState();
    if (!isPlayback) {
      return;
    }
    const dropItems = motivatorsDto.filter(this.#isDropItem);
    const upgradeItems = motivatorsDto.filter(this.#isUpgradeItem);

    if (dropItems.length) {
      const { bestDropsList, caseId, lastDropsList } = getState();

      dropItems.forEach((motivator) => {
        const isBest = motivator.data.caseRevisionPrice + MIN_PROFIT_OVERPRICE < motivator.data.winningPrice;
        setState(
          patch({
            bestDropsList: isBest ? compose(insertItem(motivator, 0), removeItem(bestDropsList.length)) : bestDropsList,
            lastDropsList: compose(insertItem(motivator, 0), removeItem(lastDropsList.length)),
          }),
        );

        if (caseId && caseId === motivator.data.case.id) {
          dispatch(new UpdateCurrentCaseMotivator(motivator.data));
        }
      });
    }

    if (upgradeItems.length) {
      const { lastUpgradesList } = getState();

      const newLastUpgradesList = reverse(upgradeItems).concat(lastUpgradesList);
      newLastUpgradesList.splice(newLastUpgradesList.length - upgradeItems.length - 1);
      setState(patch({ lastUpgradesList: newLastUpgradesList }));
    }
  }

  @Action(AddBestLastShardsWon)
  addBestLastShardsWon({ setState, getState }: StateContext<IMotivatorStateModel>, { motivatorsDto }: AddBestLastShardsWon): void {
    const { isPlayback, bestLastShardsDropList } = getState();

    if (isPlayback) {
      const newShadsItemsList = reverse(motivatorsDto).concat(bestLastShardsDropList);
      const trimmedList =
        newShadsItemsList.length > MIN_TICKETS_MOTIVATOR_ITEMS
          ? newShadsItemsList.slice(0, MIN_TICKETS_MOTIVATOR_ITEMS)
          : newShadsItemsList;
      setState(patch({ bestLastShardsDropList: trimmedList }));
    }
  }

  @Action(AddLastTicketItem)
  addLastTicketItem({ setState, getState }: StateContext<IMotivatorStateModel>, { motivatorsDto }: AddLastTicketItem): void {
    const { isPlayback, lastTicketsItemsList } = getState();

    if (isPlayback) {
      const newTicketItemsList = reverse(motivatorsDto).concat(lastTicketsItemsList);

      if (newTicketItemsList.length > 10) {
        newTicketItemsList.splice(9);
      }

      setState(patch({ lastTicketsItemsList: newTicketItemsList }));
    }
  }

  @Action(AddTradeHistoryItem)
  addTradeHistoryItem({ getState, setState }: StateContext<IMotivatorStateModel>, { motivatorsDto }: AddTradeHistoryItem): void {
    const { marketList } = getState();

    const availableStatusesForMotivator = [EP2pItemCreatedStatuses.NEW, EP2pItemCreatedStatuses.COMPLETED];
    motivatorsDto
      .filter((item) => availableStatusesForMotivator.includes(item?.data?.status))
      .forEach((motivator) => {
        motivator?.data?.items?.forEach((item: IP2pItemCreatedItem) => {
          const uniqId = uniqueId(LAST_PREFIX);
          const currentMotivatorDto: IMotivatorDto<IP2pItemCreated> = {
            ...motivator,
            data: { ...motivator.data, items: [item] },
            uniqId,
          };
          setState(patch({ marketList: compose(insertItem(currentMotivatorDto, 0), removeItem(marketList.length)) }));
        });
      });
  }

  @Action(ClearCasesMotivator)
  clearCasesMotivator({ patchState }: StateContext<IMotivatorStateModel>): void {
    patchState({
      caseId: null,
      currentCaseMotivator: MOTIVATOR_INITIAL_STATE.currentCaseMotivator,
      isCaseMotivatorHidden: false,
    });
    this.#subscribeStream?.unsubscribe();
    this.#subscribeStream = null;
  }

  /**
   * Получение мотиватора текущего кейса.
   * Запрашивает bestLast; если элементов < limit — дозапрашивает last и мержит уникальные.
   * Если после мержа < MIN_CASES_MOTIVATOR_VISIBLE_ITEMS — скрывает мотиватор.
   */
  @Action(GetCurrentCaseMotivator)
  getCurrentCaseMotivator(
    ctx: StateContext<IMotivatorStateModel>,
    { caseId, limit = MIN_CASES_MOTIVATOR_ITEMS }: GetCurrentCaseMotivator,
  ): Observable<Array<IMotivatorDto<IDropItemDto>>> {
    const { patchState } = ctx;

    if (caseId) {
      this.#subscribeStream?.unsubscribe();
      this.#subscribeStream = this.#createSocketStream(ctx);
    }

    patchState({ isCaseMotivatorHidden: false });

    const lang = this.#getLang();
    return this.#casesBackendService.getStat({ caseId, keys: MotivatorReqKeys.BEST_LAST, limit, lang }).pipe(
      switchMap(({ bestLast = [] }) => {
        if (bestLast.length >= limit) {
          return of(bestLast);
        }
        return this.#casesBackendService.getStat({ caseId, keys: MotivatorReqKeys.LAST, limit, lang }).pipe(
          map(({ last = [] }) => {
            const existingIds = new Set(bestLast.map((item) => item.data.id));
            const additional = last.filter((item) => !existingIds.has(item.data.id));
            return [...bestLast, ...additional].slice(0, limit);
          }),
        );
      }),
      tap((items) => {
        const hasEnough = items.length >= MIN_CASES_MOTIVATOR_VISIBLE_ITEMS;
        patchState({
          caseId,
          isCaseMotivatorHidden: !hasEnough,
          currentCaseMotivator: {
            ...MOTIVATOR_INITIAL_STATE.currentCaseMotivator,
            bestLast: hasEnough ? items.map((item) => ({ ...item, uniqId: uniqueId(BEST_LAST_PREFIX) })) : [],
          },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        patchState({
          currentCaseMotivator: MOTIVATOR_INITIAL_STATE.currentCaseMotivator,
          isCaseMotivatorHidden: true,
        });

        this.#onError(error);

        throw new Error(error.message);
      }),
    );
  }

  /**
   * Получение мотиватора кейсов за сутки
   */
  @Action(GetLobbyCasesMotivator)
  getLobbyCasesMotivator(ctx: StateContext<IMotivatorStateModel>, { limit = 15 }: GetLobbyCasesMotivator): Observable<IMotivatorStatistic> {
    const { patchState } = ctx;
    const keys = MotivatorReqKeys.BEST_LAST_ALL;
    const lang = this.#getLang();

    this.#subscribeStream?.unsubscribe();
    this.#subscribeStream = this.#createSocketStream(ctx);

    return this.#casesBackendService.getStat({ keys, limit, lang }).pipe(
      tap(({ bestLastAll }) => {
        const bestLastAllUniqId = bestLastAll ? bestLastAll.map((item) => ({ ...item, uniqId: uniqueId(BEST_LAST_ALL_PREFIX) })) : [];
        patchState({
          lobbyCasesMotivator: {
            bestLastAll: bestLastAllUniqId,
          },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        patchState({ lobbyCasesMotivator: MOTIVATOR_INITIAL_STATE.lobbyCasesMotivator });

        this.#onError(error);

        throw new Error(error.message);
      }),
    );
  }

  @Action(GetBestLastResultStat)
  getBestLastResultStat({ setState }: StateContext<IMotivatorStateModel>, { limit }: GetMarketStat): Observable<IMotivatorTop24Item[]> {
    return this.#motivatorBackendService
      .getMotivatorTop24({
        limit: limit ?? 20,
      })
      .pipe(
        map((response) => response.map((item) => mapTop24ItemDto(item))),
        tap((normalized) => setState(patch({ bestLastResult: normalized }))),
      );
  }

  @Action(AddBestResultStat)
  addBestResultStat({ patchState }: StateContext<IMotivatorStateModel>, { motivatorDto }: AddBestResultStat): void {
    patchState({
      bestLastResult: [...motivatorDto],
    });
  }

  @Action(GetMarketStat)
  getMarketStat(ctx: StateContext<IMotivatorStateModel>, { limit }: GetMarketStat): Observable<IP2PMotivatorStatistic> {
    this.#subscribeStream?.unsubscribe();
    this.#subscribeStream = this.#createSocketStream(ctx);
    return this.#userService.user$.pipe(
      filter((user) => user !== undefined),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
      switchMap((user) => {
        const needFilterCases = !user;
        return this.#p2pApiNewService.getMarketStat({ limit }).pipe(
          tap((data) => {
            const marketList = (data.last ?? []).reduce((acc: Array<IMotivatorDto<IP2pItemCreated>>, cur) => {
              if (needFilterCases && cur.data.items.some((item) => item.baseItem.type === 'container')) {
                return acc; // Пропускаем элементы с кейсами
              }
              const item: IMotivatorDto<IP2pItemCreated> = { ...cur, uniqId: uniqueId(LAST_PREFIX) };
              acc.push(item);
              return acc;
            }, []);
            ctx.patchState({
              ...data,
              marketList,
            });
          }),
        );
      }),
    );
  }

  @Action(GetStat)
  getStat(ctx: StateContext<IMotivatorStateModel>, { limit }: GetStat): Observable<void> {
    this.#subscribeStream?.unsubscribe();
    this.#subscribeStream = this.#createSocketStream(ctx);
    const { dispatch, patchState } = ctx;
    const keys = [
      MotivatorReqKeys.BEST_DROP,
      MotivatorReqKeys.BEST_LAST_ALL,
      ...(this.#isProjectMarket() ? [MotivatorReqKeys.BEST_LAST] : []),
      MotivatorReqKeys.BIG_OPENS,
      MotivatorReqKeys.LAST,
    ].join(',');
    const listLimit = limit || MIN_MOTIVATOR_ITEMS;
    const lang = this.#getLang();

    return this.#casesBackendService.getStat({ keys, limit: listLimit, lang }).pipe(
      tap(({ bestLast = [], bestLastAll = [], last = [], bestDrop = null, bigOpens = null }) => {
        const lastDropsList = last.map((item) => ({ ...item, uniqId: uniqueId(LAST_PREFIX) }));
        if (lastDropsList.length > listLimit) {
          lastDropsList.length = listLimit;
        }

        patchState({
          bestDropsList: bestLast.map((item) => ({ ...item, uniqId: uniqueId(BEST_LAST_PREFIX) })),
          bestDropsListAll: bestLastAll.map((item) => ({ ...item, uniqId: uniqueId(BEST_LAST_ALL_PREFIX) })),
          lastDropsList,
          bestDrop: bestDrop ? { data: bestDrop, agentKey: EAgentKey.CASES_GAME, uniqId: uniqueId(BEST_DROP) } : null,
          bigOpens: bigOpens ?? null,
        });
      }),
      switchMap((response) => {
        let payload: IActivities = {};

        [Widgets.PlayerOfTheDay, Widgets.LuckOfTheDay].forEach((widget: Widgets) => {
          const stat = this.#statsToWidget(widget, response);

          if (stat) {
            payload = Object.assign(payload, stat);
          }

          return stat;
        });

        return payload ? dispatch([new SetCurrentGameActivities(payload)]) : EMPTY;
      }),
    );
  }

  @Action(GetShardsStat)
  getShardsStat(
    { patchState }: StateContext<IMotivatorStateModel>,
    { limit }: GetShardsStat,
  ): Observable<Array<IMotivatorDto<IBonusPiecesDto>>> {
    return this.#motivatorBackendService.getMotivatorShards(limit ?? MIN_TICKETS_MOTIVATOR_ITEMS).pipe(
      tap((lastShards) => {
        const enrichedShards = lastShards.map((item) => ({
          ...item,
          uniqId: uniqueId(SHARD_PREFIX),
        }));
        patchState({ bestLastShardsDropList: enrichedShards });
      }),
    );
  }

  @Action(GetUpgraderStat)
  getUpgraderStat(ctx: StateContext<IMotivatorStateModel>, { limit }: GetUpgraderStat): Observable<IMotivatorStatisticUpgrader> {
    const keys = [MotivatorReqKeys.BEST_LAST, MotivatorReqKeys.LAST].join(',');
    const { patchState } = ctx;

    this.#subscribeStream?.unsubscribe();
    this.#subscribeStream = this.#createSocketStream(ctx);

    return this.#upgraderBackendService.getUpgraderStat({ keys, limit: limit ?? MIN_MOTIVATOR_ITEMS }).pipe(
      tap(({ bestLast = [], last = [] }) =>
        patchState({
          bestUpgradesList: bestLast.map((item) => ({ ...item, uniqId: uniqueId(BEST_LAST_PREFIX) })),
          lastUpgradesList: last.map((item) => ({ ...item, uniqId: uniqueId(LAST_PREFIX) })),
        }),
      ),
    );
  }

  @Action(ResetPartialState)
  resetPartialState({ patchState }: StateContext<IMotivatorStateModel>, { propertiesToClear }: ResetPartialState): void {
    const patchStateVal = propertiesToClear.reduce(
      (val, property) => ({ ...val, [property]: MOTIVATOR_INITIAL_STATE[property] }),
      {} satisfies Partial<IMotivatorStateModel>,
    );

    patchState(patchStateVal);
  }

  @Action(SubscribeToLastWon)
  subscribeToLastWon({ patchState }: StateContext<IMotivatorStateModel>): void {
    patchState({ isSubscribedToLastWon: true });
  }

  @Action(ToggleDropList)
  toggleDropList({ patchState }: StateContext<IMotivatorStateModel>, { isPlayback }: ToggleDropList): void {
    patchState({ isPlayback });
  }

  @Action(UnsubscribeToLastWon)
  unsubscribeToLastWon({ patchState }: StateContext<IMotivatorStateModel>): void {
    patchState({ isSubscribedToLastWon: false });
  }

  @Action(UpdateCurrentCaseMotivator)
  updateCurrentCaseMotivator({ setState, getState }: StateContext<IMotivatorStateModel>, { statistic }: UpdateCurrentCaseMotivator): void {
    if (getState().isCaseMotivatorHidden) {
      return;
    }

    const updateDelay = this.#getUpdateDelay();
    const { currentCaseMotivator } = getState();

    const bestLast = currentCaseMotivator.bestLast || [];
    const motivatorDto: IMotivatorDto<IDropItemDto> = {
      agentKey: EAgentKey.CASES_GAME,
      data: statistic,
      uniqId: uniqueId(BEST_LAST_PREFIX),
    };

    const composeBest: Array<StateOperator<Array<IMotivatorDto<IDropItemDto>>>> = [];

    composeBest.push(insertItem(motivatorDto, 0));

    if (bestLast.length > MIN_CASES_MOTIVATOR_ITEMS) {
      composeBest.push(removeItem(bestLast.length));
    }

    setTimeout(() => {
      setState(
        patch({
          currentCaseMotivator: patch({
            bestLast: compose(...composeBest),
          }),
        }),
      );
    }, updateDelay);
  }

  @Action(UpdateLobbyCasesMotivator)
  updateLobbyCasesMotivator({ setState, getState }: StateContext<IMotivatorStateModel>, { statistic }: UpdateLobbyCasesMotivator): void {
    const updateDelay = this.#getUpdateDelay();
    const { lobbyCasesMotivator } = getState();

    const bestLastAll = lobbyCasesMotivator.bestLastAll || [];
    const motivatorDto: IMotivatorDto<IDropItemDto> = {
      agentKey: EAgentKey.CASES_GAME,
      data: statistic,
      uniqId: uniqueId(BEST_LAST_ALL_PREFIX),
    };

    const composeBest: Array<StateOperator<Array<IMotivatorDto<IDropItemDto>>>> = [];

    composeBest.push(insertItem(motivatorDto, 0));

    if (bestLastAll.length > MIN_CASES_MOTIVATOR_ITEMS) {
      composeBest.push(removeItem(bestLastAll.length));
    }

    setTimeout(() => {
      if (!getState().isPlayback) {
        return;
      }
      setState(
        patch({
          lobbyCasesMotivator: patch({
            bestLastAll: compose(...composeBest),
          }),
        }),
      );
    }, updateDelay);
  }

  @Action(SetActiveMotivatorSocket)
  setActiveMotivatorSocket({ patchState, getState }: StateContext<IMotivatorStateModel>, { activeSocket }: SetActiveMotivatorSocket): void {
    patchState({ activeSockets: uniq([...getState().activeSockets, activeSocket]) });
  }

  @Action(UnsetActiveMotivatorSocket)
  unsetActiveMotivatorSocket(
    { patchState, getState }: StateContext<IMotivatorStateModel>,
    { activeSocket }: UnsetActiveMotivatorSocket,
  ): void {
    patchState({ activeSockets: getState().activeSockets.filter((socket) => socket !== activeSocket) });
  }

  #canHandleSocketEvent(socket: MotivatorSocketKeys, activeSockets: MotivatorSocketKeys[]): boolean {
    return activeSockets.includes(socket) && this.#pageInFocus();
  }

  // TODO rework it
  #statsToWidget(widget: Widgets, response: IMotivatorStatistic): any {
    if (!response) {
      return undefined;
    }
    switch (widget) {
      case 'playerOfTheDay': {
        if (response.bigOpens) {
          const { user, count } = response.bigOpens;
          return user && count
            ? {
                playerOfTheDay: {
                  header: 'LOCAL.CASES.MOTIVATOR.TOP_PLAYER',
                  avaLink: user.avatar,
                  id: user.id,
                  name: user.name,
                  register: user.registered,
                  payload: [
                    {
                      label: 'LOCAL.CASES.MOTIVATOR.OPENED',
                      value: count,
                      valueType: 'number',
                    },
                  ],
                },
              }
            : undefined;
        }
        return undefined;
      }
      case 'luckOfTheDay': {
        if (response.bestDrop) {
          const bestDrop = response.bestDrop;
          const { user, caseRevisionItem, winningPrice, caseRevisionPrice } = bestDrop;
          return user && caseRevisionItem
            ? {
                luckOfTheDay: {
                  header: 'RECENT_ACTIVITIES_WIDGET.LUCK_OF_THE_DAY',
                  avaLink: user.avatar,
                  id: user.id,
                  name: user.name,
                  height: '24.5rem',
                  replayDrop: {
                    id: user.id,
                    case: bestDrop.case,
                    caseRevisionItem: caseRevisionItem,
                    caseRevisionPrice: caseRevisionPrice,
                    winningPrice: winningPrice,
                    user: user,
                    rollId: bestDrop.rollId,
                    createdAt: bestDrop.createdAt,
                    profit: bestDrop.profit,
                    openUUID: bestDrop.openUUID,
                  },
                  payload: [
                    // {
                    //   label: 'WIN',
                    //   value: winningPrice,
                    //   valueType: 'currency',
                    // },
                    {
                      label: 'LOCAL.CASES.BATTLE_GAME.BATTLE.WINNER',
                      value: winningPrice,
                      valueType: 'user',
                    },
                    {
                      label: 'CHANCE',
                      value: caseRevisionItem.count,
                      valueType: 'percent',
                    },
                  ],
                },
              }
            : undefined;
        }
        return undefined;
      }
      default:
        return undefined;
    }
  }

  // Прочие методы
  #isDropItem(motivatorDto: IMotivatorDto<IDropItemDto | IUpgraderItem>): motivatorDto is IMotivatorDto<IDropItemDto> {
    return motivatorDto.agentKey === EAgentKey.CASES_GAME;
  }

  #isUpgradeItem(motivatorDto: IMotivatorDto<IDropItemDto | IUpgraderItem>): motivatorDto is IMotivatorDto<IUpgraderItem> {
    return motivatorDto.agentKey === EAgentKey.UPGRADE;
  }

  #shardGuard(motivatorDto: IMotivatorDto<IBonusPiecesDto | IDropItemDto>): motivatorDto is IMotivatorDto<IBonusPiecesDto> {
    const isBonus = motivatorDto.agentKey === EAgentKey.BONUS;
    const isMotivatorTypeBonusWallet = motivatorDto.motivatorType === MotivatorReqTypes.BONUS_WALLET;
    const isGetFromButton = 'bonusPieces' in motivatorDto.data ? motivatorDto.data.typeId === MotivatorSource.FROM_BUTTON : false;
    return isBonus && isMotivatorTypeBonusWallet && isGetFromButton;
  }

  #isMarketItem(
    motivatorDto: IMotivatorDto<IDropItemDto | IUpgraderItem | IP2pItemCreated>,
  ): motivatorDto is IMotivatorDto<IP2pItemCreated> {
    return motivatorDto.agentKey === EAgentKey.MARKET;
  }

  #bufferMotivatorsWhenPageIsUnactive<T>(): OperatorFunction<T, T[]> {
    return (source$: Observable<T>) => {
      const visibility$ = this.#visibilityChange$.pipe(shareReplay({ bufferSize: 1, refCount: true }));
      const sharedSource$ = source$.pipe(share());

      const activeEvents$ = sharedSource$.pipe(
        withLatestFrom(visibility$),
        filter(([, isActive]) => isActive),
        map(([value]) => [value]),
      );

      const inactiveEventsBuffer$ = sharedSource$.pipe(
        withLatestFrom(visibility$),
        filter(([, isActive]) => !isActive),
        map(([value]) => value),
        buffer(visibility$.pipe(filter((isActive) => isActive))),
        map((buf) => (buf.length > 15 ? buf.slice(buf.length - 15) : buf)),
        filter((buf) => buf.length > 0),
      );

      return merge(activeEvents$, inactiveEventsBuffer$);
    };
  }

  #createSocketStream({ dispatch, getState }: StateContext<IMotivatorStateModel>): Subscription | null {
    if (this.#isServer) {
      return null;
    }

    return this.#userService.user$
      .pipe(
        filter((user) => user !== undefined),
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        switchMap((user) => {
          const lastWon$ = this.#motivatorSocketService
            .onLastWon$()
            .pipe(filter(() => getState().isSubscribedToLastWon && getState().activeSockets.includes(MotivatorSocketKeys.LAST_WON)));

          return merge(
            this.#motivatorSocketService.onBestLastAllWon$().pipe(
              tap((motivatorDto) => {
                if (!this.#canHandleSocketEvent(MotivatorSocketKeys.BEST_LAST_ALL_WON, getState().activeSockets)) {
                  return;
                }

                dispatch(new UpdateLobbyCasesMotivator(motivatorDto.data));

                if (getState().isSubscribedToLastWon) {
                  const uniqId = uniqueId(BEST_LAST_ALL_PREFIX);

                  dispatch(new AddBestLastAllWon({ ...motivatorDto, uniqId }));
                }
              }),
            ),
            this.#motivatorSocketService.onBestLastWon$().pipe(
              filter(() => getState().activeSockets.includes(MotivatorSocketKeys.BEST_LAST_WON)),
              this.#bufferMotivatorsWhenPageIsUnactive(),
              tap((motivatorDto) => {
                const payload = motivatorDto.map((motivator) => ({ ...motivator, uniqId: uniqueId(LAST_PREFIX) }));
                const shardItems = payload.filter((motivator) => this.#shardGuard(motivator));
                const notShardItems = payload.filter((motivator): motivator is IMotivatorDto<IDropItemDto> => !this.#shardGuard(motivator));

                if (shardItems.length) {
                  dispatch(new AddBestLastShardsWon(shardItems));
                }
                dispatch(new AddBestLastWon(notShardItems));
              }),
            ),
            lastWon$.pipe(
              filter((motivatorDto) => [EAgentKey.CASES_GAME, EAgentKey.UPGRADE].includes(motivatorDto.agentKey)),
              this.#bufferMotivatorsWhenPageIsUnactive(),
              tap((motivatorDto) => {
                const payload = motivatorDto.map((motivator) => ({ ...motivator, uniqId: uniqueId(LAST_PREFIX) }));
                const bonusWallet = payload.filter((motivator) => motivator.motivatorType === MotivatorReqTypes.BONUS_WALLET_ITEM);
                const notBonusWallet = payload.filter((motivator) => motivator.motivatorType !== MotivatorReqTypes.BONUS_WALLET_ITEM);

                if (bonusWallet.length) {
                  dispatch(new AddLastTicketItem(bonusWallet));
                }
                dispatch(new AddLastWon(notBonusWallet));
              }),
            ),
            lastWon$.pipe(
              filter((motivatorDto) => [EAgentKey.MARKET].includes(motivatorDto.agentKey) && this.#isMarketItem(motivatorDto)),
              this.#bufferMotivatorsWhenPageIsUnactive(),
              tap((motivatorDto) => {
                dispatch(new AddTradeHistoryItem(motivatorDto));
              }),
            ),
            this.#motivatorSocketService.onTop24Results$().pipe(
              tap((payload) => {
                const mapped = payload.topWins24.map((item) => mapTop24ItemDto(item));
                if (!this.#canHandleSocketEvent(MotivatorSocketKeys.TOP_24, getState().activeSockets)) {
                  return;
                }
                dispatch(new AddBestResultStat(mapped));
              }),
            ),
          );
        }),
      )
      .subscribe();
  }

  #onError({ error }: HttpErrorResponse): Observable<never> {
    this.#notificationsService.addNotification({
      createDate: Date.now(),
      icon: 'warning',
      id: Date.now(),
      message: (error && error.message) ?? (typeof error === 'string' ? error : 'Error'),
      status: NotificationStatus.new,
      system: true,
      type: (error && error.type) ?? NotificationType.Error,
    });

    return EMPTY;
  }

  #getUpdateDelay(): number {
    const animationSpeed: AnimationSpeed = this.#store.selectSnapshot(MediaPreferencesState.animationSpeed);
    const animationDuration = OPENING_ANIM_DURATION[this.#environmentService.getEnvironment().PROJECT as PROJECT]?.[animationSpeed] || 0;
    const updateDelay = animationDuration + OUT_FROM_ANIMATION_DURATION;
    return updateDelay;
  }
}
